import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Music as MusicIcon, Download, FileText, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { AudioPlayer } from "@/components/AudioPlayer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Album {
  id: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  published: boolean;
}

interface Disk {
  id: string;
  album_id: string;
  disk_number: number;
  disk_name: string | null;
}

interface Song {
  id: string;
  disk_id: string;
  title: string;
  duration: string | null;
  audio_file_path: string;
  lyrics: string | null;
  chord_sheet_path: string | null;
  display_order: number;
  published: boolean;
  song_number: number | null;
  description: string | null;
}

interface DiskGroup {
  disk: Disk;
  songs: Song[];
}

const Music = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, loading: authLoading, isMember, isAdmin, isMusicEditor, isMusicViewer } = useAuth();
  const { toast } = useToast();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [songs, setSongs] = useState<Record<string, DiskGroup[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [isLyricsDialogOpen, setIsLyricsDialogOpen] = useState(false);
  const [playingSongId, setPlayingSongId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Russian pluralization helper for proper declension
  const getPluralForm = (count: number) => {
    if (language !== 'ru') {
      return count === 1 ? t("music.song") : t("music.songs2-4");
    }
    
    const n = Math.abs(count);
    const n10 = n % 10;  // Last digit
    const n100 = n % 100; // Last two digits
    
    // Rule 1: Numbers ending in 1 (but not 11): 1, 21, 31, 41, 51, 101, 121, etc. → песня
    if (n10 === 1 && n100 !== 11) {
      return t("music.song");
    } 
    // Rule 2: Numbers ending in 2, 3, 4 (but not 12, 13, 14): 2-4, 22-24, 32-34, 42-44, etc. → песни
    else if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) {
      return t("music.songs2-4");
    } 
    // Rule 3: All other cases: 0, 5-20, 25-30, 35-40, 45-50, etc. → песен
    else {
      return t("music.songs5+");
    }
  };

  useEffect(() => {
    // Wait for auth to load before checking permissions
    if (authLoading) {
      return;
    }

    // Redirect if not authenticated or not a member/music viewer/music editor
    if (!user || (!isMember && !isAdmin && !isMusicEditor && !isMusicViewer)) {
      toast({
        title: t("music.accessDenied"),
        description: t("music.accessDeniedDesc"),
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    fetchAlbumsAndSongs();
  }, [authLoading, user, isMember, isAdmin, isMusicEditor, isMusicViewer, language, navigate, toast, t]);

  const fetchAlbumsAndSongs = async () => {
    setLoading(true);
    try {
      // Fetch albums
      const { data: albumsData, error: albumsError } = await supabase
        .from("albums")
        .select("*")
        .eq("published", true)
        .order("title");

      if (albumsError) throw albumsError;

      // Fetch album translations
      let translatedAlbums = albumsData || [];
      if (albumsData && albumsData.length > 0) {
        const { data: albumTransData } = await supabase
          .from("albums_translations")
          .select("*")
          .in("album_id", albumsData.map(a => a.id))
          .eq("language", language);
        
        translatedAlbums = albumsData.map(album => {
          const trans = albumTransData?.find(t => t.album_id === album.id);
          return {
            ...album,
            title: trans?.title || album.title,
            description: trans?.description || album.description,
          };
        });
      }

      // Fetch all disks
      const { data: disksData } = await supabase
        .from("disks")
        .select("*")
        .order("disk_number");

      // Fetch disk translations
      let translatedDisks = disksData || [];
      if (disksData && disksData.length > 0) {
        const { data: diskTransData } = await supabase
          .from("disks_translations")
          .select("*")
          .in("disk_id", disksData.map(d => d.id))
          .eq("language", language);
        
        translatedDisks = disksData.map(disk => {
          const trans = diskTransData?.find(t => t.disk_id === disk.id);
          return {
            ...disk,
            disk_name: trans?.disk_name || disk.disk_name,
          };
        });
      }

      // Fetch songs
      const { data: songsData, error: songsError } = await supabase
        .from("songs")
        .select("*")
        .eq("published", true)
        .order("song_number", { nullsFirst: false })
        .order("display_order");

      if (songsError) throw songsError;
      
      // Fetch song translations
      let translatedSongs = songsData || [];
      if (songsData && songsData.length > 0) {
        const { data: transData } = await supabase
          .from("songs_translations")
          .select("*")
          .in("song_id", songsData.map(s => s.id))
          .eq("language", language);
        
        translatedSongs = songsData.map(song => {
          const trans = transData?.find(t => t.song_id === song.id);
          return {
            ...song,
            title: trans?.title || song.title,
            lyrics: trans?.lyrics || song.lyrics,
            description: trans?.description || song.description,
          };
        });
      }

      setAlbums(translatedAlbums);

      // Group songs by album and disk
      const songsByAlbum: Record<string, DiskGroup[]> = {};
      translatedSongs.forEach((song) => {
        const disk = translatedDisks.find(d => d.id === song.disk_id);
        if (!disk) return;
        
        const albumId = disk.album_id;
        if (!songsByAlbum[albumId]) {
          songsByAlbum[albumId] = [];
        }
        
        // Find existing disk group or create new one
        let diskGroup = songsByAlbum[albumId].find(g => g.disk.id === disk.id);
        
        if (!diskGroup) {
          diskGroup = {
            disk: disk,
            songs: []
          };
          songsByAlbum[albumId].push(diskGroup);
        }
        
        diskGroup.songs.push(song);
      });

      // Sort groups by disk number
      Object.keys(songsByAlbum).forEach(albumId => {
        songsByAlbum[albumId].sort((a, b) => a.disk.disk_number - b.disk.disk_number);
      });

      setSongs(songsByAlbum);
    } catch (error: any) {
      toast({
        title: t("music.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async (song: Song) => {
    // If clicking the same song, toggle player
    if (playingSongId === song.id) {
      setPlayingSongId(null);
      setAudioUrl(null);
      return;
    }

    const { data } = await supabase.storage
      .from("music-files")
      .createSignedUrl(song.audio_file_path, 3600);

    if (data?.signedUrl) {
      setAudioUrl(data.signedUrl);
      setPlayingSongId(song.id);
      setSelectedSong(song);
    }
  };

  const downloadAudio = async (song: Song) => {
    const { data } = await supabase.storage
      .from("music-files")
      .createSignedUrl(song.audio_file_path, 60);

    if (data?.signedUrl) {
      const link = document.createElement("a");
      link.href = data.signedUrl;
      link.download = `${song.title}.mp3`;
      link.click();
    }
  };

  const showLyrics = (song: Song) => {
    setSelectedSong(song);
    setIsLyricsDialogOpen(true);
  };

  const downloadChordSheet = async (song: Song) => {
    if (!song.chord_sheet_path) return;

    const { data } = await supabase.storage
      .from("music-files")
      .createSignedUrl(song.chord_sheet_path, 60);

    if (data?.signedUrl) {
      const link = document.createElement("a");
      link.href = data.signedUrl;
      link.download = `${song.title}-chords.pdf`;
      link.click();
    }
  };

  if (authLoading || loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background pt-24 pb-12">
          <div className="container mx-auto px-4">
            <Skeleton className="h-12 w-64 mb-8" />
            <div className="grid gap-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <MusicIcon className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">{t("music.title")}</h1>
          </div>

          {albums.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MusicIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">{t("music.noMusicAvailable")}</p>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {albums.map((album) => (
                <AccordionItem key={album.id} value={album.id} className="border rounded-lg">
                  <AccordionTrigger className="px-6 hover:no-underline">
                    <div className="flex items-center gap-4">
                      {album.cover_image_url && (
                        <img
                          src={album.cover_image_url}
                          alt={album.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="text-left">
                        <h3 className="text-xl font-semibold">{album.title}</h3>
                        {album.description && (
                          <p className="text-sm text-muted-foreground">{album.description}</p>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <Accordion type="single" collapsible className="space-y-4 mt-4">
                      {songs[album.id]?.map((diskGroup, idx) => {
                        const diskLabel = diskGroup.disk.disk_name;
                        const fullLabel = diskLabel 
                          ? `Disk ${diskGroup.disk.disk_number} - ${diskLabel}`
                          : `Disk ${diskGroup.disk.disk_number}`;
                        const diskKey = `disk-${album.id}-${idx}`;
                        
                        return (
                          <AccordionItem key={diskKey} value={diskKey} className="border rounded-lg">
                            <AccordionTrigger className="px-4 py-3 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <MusicIcon className="w-5 h-5 text-primary" />
                                <span className="font-semibold">{fullLabel}</span>
                                <span className="text-sm text-muted-foreground">
                                  ({diskGroup.songs.length} {getPluralForm(diskGroup.songs.length)})
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-3 mt-2">
                                {diskGroup.songs.map((song) => (
                                  <Card key={song.id} className="p-4">
                                    <div className="space-y-3">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            {song.song_number && (
                                              <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded">
                                                #{song.song_number}
                                              </span>
                                            )}
                                            <h4 className="font-semibold">{song.title}</h4>
                                          </div>
                                          {song.description && (
                                            <p className="text-sm text-muted-foreground mt-1">{song.description}</p>
                                          )}
                                          {song.duration && (
                                            <p className="text-xs text-muted-foreground">{song.duration}</p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Button
                                            size="sm"
                                            variant={playingSongId === song.id ? "default" : "outline"}
                                            onClick={() => playAudio(song)}
                                          >
                                            <MusicIcon className="w-4 h-4" />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => downloadAudio(song)}
                                          >
                                            <Download className="w-4 h-4" />
                                          </Button>
                                          {song.lyrics && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => showLyrics(song)}
                                            >
                                              <FileText className="w-4 h-4" />
                                            </Button>
                                          )}
                                          {song.chord_sheet_path && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => downloadChordSheet(song)}
                                              title="Download Music Sheet"
                                            >
                                              <Users className="w-4 h-4" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                      {playingSongId === song.id && audioUrl && (
                                        <AudioPlayer 
                                          src={audioUrl} 
                                          title={song.title}
                                          onEnded={() => {
                                            setPlayingSongId(null);
                                            setAudioUrl(null);
                                          }}
                                        />
                                      )}
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </main>
      <Footer />

      {/* Lyrics Dialog */}
      <Dialog open={isLyricsDialogOpen} onOpenChange={setIsLyricsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSong?.title} - {t("music.lyrics")}</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap">{selectedSong?.lyrics}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Music;